import {
  ChevronDownIcon,
  UserGroupIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  SunIcon,
  Squares2X2Icon,
  Cog6ToothIcon,
  CodeBracketIcon,
  BeakerIcon,
} from '@heroicons/react/24/outline';
import useTeams from 'hooks/useTeams';
import useApps from 'hooks/useApps';
import useApp from 'hooks/useApp';
import { useSession } from 'next-auth/react';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React from 'react';
import { useCustomSignOut } from 'hooks/useCustomSignout';
import useTheme from 'hooks/useTheme';
import env from '@/lib/env';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';

const Breadcrumb = () => {
  const router = useRouter();
  const { teams } = useTeams();
  const { data } = useSession();
  const { t } = useTranslation('common');
  const signOut = useCustomSignOut();
  const { toggleTheme } = useTheme();

  const { slug, appId } = router.query as { slug?: string; appId?: string };

  const currentTeam = (teams || []).find((team) => team.slug === slug);

  // 获取当前 app 和所有 apps
  const { app: currentApp } = useApp(slug, appId);
  const { apps } = useApps(slug);

  // 判断当前是在哪个页面
  const isAppsPage = router.pathname.includes('/apps');
  const isUserSettingsPage = router.pathname.includes('/settings') && !slug;
  const isAppDetailPage = isAppsPage && !!appId;

  // 判断是否在团队设置相关页面（包括 settings、members、webhooks 等所有非 apps 的团队页面）
  const isTeamSettingsArea = slug && !isAppsPage && !isUserSettingsPage;

  // 获取当前选中的部分
  const currentSection = isAppsPage
    ? 'apps'
    : isTeamSettingsArea
      ? 'settings'
      : null;

  return (
    <div className="flex items-center justify-between w-full gap-4">
      {/* 左侧面包屑 */}
      <nav className="flex items-center space-x-1 sm:space-x-2 text-sm flex-1 min-w-0">
        {/* Team 选择器 */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="flex items-center gap-1.5 px-3 py-1.5 h-auto font-medium"
            >
              <UserGroupIcon className="w-4 h-4" />
              <span>{currentTeam ? currentTeam.name : t('teams')}</span>
              <ChevronDownIcon className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-64" align="start">
            {teams && teams.length > 0 && (
              <>
                <DropdownMenuLabel>{t('teams')}</DropdownMenuLabel>
                {teams.map((team) => (
                  <DropdownMenuItem key={team.id} asChild>
                    <Link
                      href={`/teams/${team.slug}/apps`}
                      className={cn(
                        'flex items-center gap-2',
                        team.id === currentTeam?.id && 'bg-accent'
                      )}
                    >
                      <UserGroupIcon className="w-4 h-4" />
                      <span>{team.name}</span>
                    </Link>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem asChild>
              <Link href="/teams">{t('all-teams')}</Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* 分隔符和二级导航 */}
        {currentTeam && currentSection && (
          <>
            <span className="text-muted-foreground select-none">/</span>

            {/* Apps / Settings 下拉菜单 */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center gap-1.5 px-3 py-1.5 h-auto font-medium"
                >
                  {currentSection === 'apps' ? (
                    <>
                      <Squares2X2Icon className="w-4 h-4" />
                      <span>{t('apps')}</span>
                    </>
                  ) : (
                    <>
                      <Cog6ToothIcon className="w-4 h-4" />
                      <span>{t('settings')}</span>
                    </>
                  )}
                  <ChevronDownIcon className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-48" align="start">
                <DropdownMenuItem asChild>
                  <Link
                    href={`/teams/${slug}/apps`}
                    className={cn(
                      'flex items-center gap-2',
                      isAppsPage && 'bg-accent'
                    )}
                  >
                    <Squares2X2Icon className="w-4 h-4" />
                    <span>{t('apps')}</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link
                    href={`/teams/${slug}/settings`}
                    className={cn(
                      'flex items-center gap-2',
                      isTeamSettingsArea && 'bg-accent'
                    )}
                  >
                    <Cog6ToothIcon className="w-4 h-4" />
                    <span>{t('settings')}</span>
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}

        {/* App 详情页面的第三级导航 */}
        {isAppDetailPage && currentApp && (
          <>
            <span className="text-muted-foreground select-none">/</span>

            {/* App 下拉菜单 */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center gap-2 px-2 py-1.5 h-auto font-medium max-w-[240px]"
                >
                  <div
                    className="flex items-center justify-center w-8 h-8 rounded-md text-lg flex-shrink-0"
                    style={{
                      backgroundColor: currentApp.iconColor || '#6366f1',
                    }}
                  >
                    {currentApp.emoji || '📱'}
                  </div>
                  <span className="truncate">{currentApp.name}</span>
                  <ChevronDownIcon className="w-4 h-4 flex-shrink-0" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-64 max-h-96 overflow-y-auto"
                align="start"
              >
                {apps && apps.length > 0 ? (
                  <>
                    <DropdownMenuLabel>{t('apps')}</DropdownMenuLabel>
                    {apps.map((app) => (
                      <DropdownMenuItem key={app.id} asChild>
                        <Link
                          href={`/teams/${slug}/apps/${app.id}`}
                          className={cn(
                            'flex items-center gap-3',
                            app.id === currentApp.id && 'bg-accent'
                          )}
                        >
                          <div
                            className="flex items-center justify-center w-10 h-10 rounded-lg text-xl flex-shrink-0 shadow-sm"
                            style={{
                              backgroundColor: app.iconColor || '#6366f1',
                            }}
                          >
                            {app.emoji || '📱'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">
                              {app.name}
                            </div>
                            {app.description && (
                              <div className="text-xs text-muted-foreground truncate">
                                {app.description}
                              </div>
                            )}
                          </div>
                        </Link>
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link
                        href={`/teams/${slug}/apps`}
                        className="flex items-center gap-2 text-primary"
                      >
                        <Squares2X2Icon className="w-4 h-4" />
                        {t('back-to-apps')}
                      </Link>
                    </DropdownMenuItem>
                  </>
                ) : (
                  <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                    {t('no-apps-title')}
                  </div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* App 功能导航 */}
            <span className="text-muted-foreground select-none">/</span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center gap-2 px-2 py-1.5 h-auto font-medium"
                >
                  {router.pathname ===
                  `/teams/[slug]/apps/[appId]/orchestrate` ? (
                    <BeakerIcon className="w-4 h-4 flex-shrink-0" />
                  ) : router.pathname ===
                    `/teams/[slug]/apps/[appId]/develop` ? (
                    <CodeBracketIcon className="w-4 h-4 flex-shrink-0" />
                  ) : (
                    <BeakerIcon className="w-4 h-4 flex-shrink-0" />
                  )}
                  <span>
                    {router.pathname ===
                    `/teams/[slug]/apps/[appId]/orchestrate`
                      ? 'Orchestrate'
                      : router.pathname === `/teams/[slug]/apps/[appId]/develop`
                        ? 'Develop'
                        : 'Features'}
                  </span>
                  <ChevronDownIcon className="w-4 h-4 flex-shrink-0" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-48" align="start">
                <DropdownMenuItem asChild>
                  <Link
                    href={`/teams/${slug}/apps/${appId}/orchestrate`}
                    className={cn(
                      'flex items-center gap-2',
                      router.pathname ===
                        `/teams/[slug]/apps/[appId]/orchestrate` &&
                        'bg-accent text-primary'
                    )}
                  >
                    <BeakerIcon className="w-4 h-4" />
                    {t('orchestrate')}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link
                    href={`/teams/${slug}/apps/${appId}/develop`}
                    className={cn(
                      'flex items-center gap-2',
                      router.pathname ===
                        `/teams/[slug]/apps/[appId]/develop` &&
                        'bg-accent text-primary'
                    )}
                  >
                    <CodeBracketIcon className="w-4 h-4" />
                    {t('develop')}
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}

        {/* 用户设置页面的面包屑 */}
        {isUserSettingsPage && (
          <>
            <span className="text-muted-foreground select-none">/</span>
            <span className="px-3 py-1.5 font-medium">{t('settings')}</span>
          </>
        )}
      </nav>

      {/* 右侧用户菜单 */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="relative h-8 w-8 rounded-full p-0">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-gradient-to-br from-indigo-500 to-purple-500 text-white text-sm font-medium">
                {data?.user?.name?.[0]?.toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56" align="end">
          <DropdownMenuLabel>
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-medium leading-none">
                {data?.user?.name}
              </p>
              <p className="text-xs leading-none text-muted-foreground truncate">
                {data?.user?.email}
              </p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link href="/settings/account" className="flex items-center gap-2">
              <UserCircleIcon className="w-4 h-4" />
              <span>{t('profile')}</span>
            </Link>
          </DropdownMenuItem>
          {env.darkModeEnabled && (
            <DropdownMenuItem onClick={toggleTheme} className="cursor-pointer">
              <SunIcon className="w-4 h-4 mr-2" />
              <span>{t('switch-theme')}</span>
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={signOut}
            className="cursor-pointer text-destructive focus:text-destructive"
          >
            <ArrowRightOnRectangleIcon className="w-4 h-4 mr-2" />
            <span>{t('logout')}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default Breadcrumb;
